import React, { useState, useRef, useEffect } from "react";
import styles from "./CustomDropdownGlobalWhite.module.scss"


interface Country {
  country: string;
  code: string;
  countryCode: string;
}

interface CountryDropdownGlobalProps {
  onChangeFunc?: (value: string) => void;
  countries?: Country[];
  countryCode: string;
  updateCountryCode?: (value: string) => void;
  additionalStyle?: string;
  isDark: boolean;
  disabled?: boolean;
  isSpecificPage?: boolean;
  isBrand?: boolean;

}

export default function CountryDropdownGlobalWhite({
  countries,
  countryCode,
  updateCountryCode,
  additionalStyle = "",
  isDark,
  disabled = false,
  isSpecificPage = false,
  isBrand=false
}: CountryDropdownGlobalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const scrollInterval = useRef<NodeJS.Timeout | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  const updateScrollState = () => {
    if (!listRef.current) return;
    const el = listRef.current;
    setAtTop(el.scrollTop === 0);
    setAtBottom(el.scrollHeight - el.scrollTop <= el.clientHeight + 1);
  };

  const startScroll = (direction: "up" | "down") => {
    const amount = direction === "up" ? -5 : 5;
    scrollInterval.current = setInterval(() => {
      if (listRef.current) {
        listRef.current.scrollBy({ top: amount });
        updateScrollState();
      }
    }, 20);
  };

  const stopScroll = () => {
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (/^[a-zA-Z ]$/.test(e.key)) {
        setSearchTerm((prev) => prev + e.key);
      } else if (e.key === "Backspace") {
        setSearchTerm((prev) => prev.slice(0, -1));
      } else if (e.key === "Escape") {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleSelect = (country) => {
    updateCountryCode(country.code);
    setIsOpen(false);
    setSearchTerm("");
  };

  const filteredCountries = countries.filter((c) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    if (term.length === 1) {
      return c.country.toLowerCase().startsWith(term);
    }
    return c.country.toLowerCase().includes(term);
  });

  useEffect(() => {
    if (listRef.current) {
      updateScrollState();
      listRef.current.addEventListener("scroll", updateScrollState);
    }
    return () => {
      if (listRef.current) {
        listRef.current.removeEventListener("scroll", updateScrollState);
      }
    };
  }, []);

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      <div
        id="country"
        tabIndex={0}
        onClick={toggleDropdown}
        className={`rounded-[0.83333333rem] ${isBrand?"!py-4":""} ${styles.dropdownToggle} ${additionalStyle} ${isSpecificPage?styles.darkspecific:isDark ? styles.darkBg : ""} ${disabled ? styles.disabled : ""}`}>
        <div className={`${styles.countryCode} ${isDark ? styles.darkText : ""}`}>
          {countries.find((c) => c.code == countryCode)?.countryCode || ""} {countryCode}
        </div>
        <div>
          <svg
            width="12"
            height="12"
            viewBox="0 0 13 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 1L6.62222 7L0.999999 1"
              stroke={isDark ? "#fff" : "#686868"}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className={`${styles.dropdownMenuWrapper} ${isDark ? styles.darkMode : ""}`} id="country-code-list">
          <div
            className={`${styles.scrollArrow} ${isDark ? styles.darkBg : ""}`}
            onMouseEnter={() => startScroll("up")}
            onMouseLeave={stopScroll}
            style={{ visibility: atTop ? "hidden" : "visible" }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 13L10 8L15 13"
                stroke={isDark ? "#fff" : "black"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className={styles.dropdownMenu} ref={listRef}>
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.countryCode}
                  onClick={() => handleSelect(country)}
                  className={styles.dropdownItem}
                >
                  <span className={styles.spanWidth} id={`country-${country.countryCode}`}>
                    {countryCode === country.code ? "✓" : "\u00A0"} {country.country} {country.code}
                  </span>
                </div>
              ))
            ) : (
              <li className={styles.liContainer}>No results found</li>
            )}
          </div>
          <div
            className={`${styles.scrollArrow} ${isDark ? styles.darkBg : ""}`}
            onMouseEnter={() => startScroll("down")}
            onMouseLeave={stopScroll}
            style={{ visibility: atBottom ? "hidden" : "visible" }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7L10 12L15 7"
                stroke={isDark ? "#fff" : "black"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}
